import React, { useEffect, useState } from 'react';
import api from '../api';

const TABS = ['Quotes', 'Inquiries'];

const EMPTY_QUOTE = {
  quote_number: '', customer_id: '', status: 'draft', value: '', expiry_date: '', notes: ''
};

const EMPTY_OPP = {
  customer_id: '', title: '', stage: 'new_inquiry', estimated_value: '', expected_close: '', notes: ''
};

const INQUIRY_STAGES = [
  { value: 'new_inquiry', label: 'New Inquiry' },
  { value: 'qualifying', label: 'Qualifying' },
  { value: 'quote_sent', label: 'Quote Sent' },
  { value: 'following_up', label: 'Following Up' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

function QuoteModal({ quote, customers, onClose, onSaved }) {
  const [form, setForm] = useState(quote || EMPTY_QUOTE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = !!quote;
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const save = async () => {
    if (!form.quote_number.trim()) return setError('Quote number is required');
    if (!form.customer_id) return setError('Customer is required');
    setSaving(true);
    try {
      const payload = {
        quote_number: form.quote_number,
        customer_id: form.customer_id ? parseInt(form.customer_id) : null,
        status: form.status,
        value: form.value !== '' ? parseFloat(form.value) : null,
        expiry_date: form.expiry_date || null,
        notes: form.notes,
      };
      if (isEdit) await api.patch(`/quotes/${quote.id}`, payload);
      else await api.post('/quotes', payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Quote' : 'New Quote'}</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-grid">
            <div className="form-group">
              <label>Quote Number *</label>
              <input
                value={form.quote_number}
                onChange={e => set('quote_number', e.target.value)}
                placeholder="e.g. Q-2026-001"
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Customer *</label>
            <select value={form.customer_id} onChange={e => set('customer_id', e.target.value)}>
              <option value="">Select customer...</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Expiry Date</label>
            <input type="date" value={form.expiry_date || ''} onChange={e => set('expiry_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Value (£)</label>
            <input type="number" step="0.01" value={form.value || ''} onChange={e => set('value', e.target.value)} placeholder="0.00" />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Quote'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OppModal({ opp, customers, onClose, onSaved }) {
  const [form, setForm] = useState(opp || EMPTY_OPP);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = !!opp;
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const save = async () => {
    if (!form.customer_id) return setError('Customer is required');
    if (!form.title) return setError('Title is required');
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id ? parseInt(form.customer_id) : null,
        title: form.title,
        stage: form.stage,
        estimated_value: form.estimated_value !== '' ? parseFloat(form.estimated_value) : null,
        expected_close: form.expected_close || null,
        notes: form.notes,
      };
      if (isEdit) await api.patch(`/opportunities/${opp.id}`, payload);
      else await api.post('/opportunities', payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Inquiry' : 'New Inquiry'}</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label>Customer *</label>
            <select value={form.customer_id} onChange={e => set('customer_id', e.target.value)}>
              <option value="">Select customer...</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Shopfront glazing — High St" />
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Stage</label>
              <select value={form.stage} onChange={e => set('stage', e.target.value)}>
                {INQUIRY_STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Estimated Value (£)</label>
              <input type="number" step="0.01" value={form.estimated_value || ''} onChange={e => set('estimated_value', e.target.value)} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>Expected Close</label>
              <input type="date" value={form.expected_close || ''} onChange={e => set('expected_close', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Inquiry'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Sales() {
  const [tab, setTab] = useState('Quotes');
  const [quotes, setQuotes] = useState([]);
  const [opps, setOpps] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleDeleteQuote = async (id) => {
    if (!window.confirm('Delete this quote? This cannot be undone.')) return;
    try {
      await api.delete(`/quotes/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleDeleteOpp = async (id) => {
    if (!window.confirm('Delete this opportunity? This cannot be undone.')) return;
    try {
      await api.delete(`/opportunities/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/quotes'),
      api.get('/opportunities'),
      api.get('/customers')
    ]).then(([q, o, c]) => {
      setQuotes(q.data.value || []);
      setOpps(o.data.value || []);
      setCustomers(c.data.value || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filteredQuotes = quotes.filter(q =>
    !search ||
    q.quote_number?.toLowerCase().includes(search.toLowerCase()) ||
    q.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredOpps = opps.filter(o =>
    !search ||
    o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.title?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPipeline = filteredOpps
    .filter(o => !['won', 'lost'].includes(o.stage))
    .reduce((sum, o) => sum + Number(o.estimated_value || 0), 0);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">SA<span>LES</span></h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          + New {tab === 'Quotes' ? 'Quote' : 'Inquiry'}
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 24px',
            fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600,
            letterSpacing: 1, textTransform: 'uppercase',
            color: tab === t ? 'var(--accent)' : 'var(--text-muted)',
            borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -1, transition: 'all 0.15s'
          }}>
            {t}
          </button>
        ))}
      </div>

      <div className="search-bar">
        <input
          placeholder={tab === 'Quotes' ? 'Search by quote number or customer...' : 'Search by customer or title...'}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {tab === 'Opportunities' && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            Active Inquiries: £{totalPipeline.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
          </span>
        )}
      </div>

      <div className="card">
        {loading ? <div className="loading">Loading...</div> : (
          tab === 'Quotes' ? (
            filteredQuotes.length === 0 ? <div className="empty-state">No quotes found</div> : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Quote #</th>
                      <th>Customer</th>
                      <th>Status</th>
                      <th>Total</th>
                      <th>Valid Until</th>
                      <th>Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQuotes.map(q => (
                      <tr key={q.id}>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}>
                          {q.quote_number || `Q-${String(q.id).padStart(4, '0')}`}
                        </td>
                        <td style={{ fontWeight: 600 }}>{q.customer_name || '—'}</td>
                        <td><span className={`badge badge-${q.status}`}>{q.status}</span></td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>
                          {q.value ? `£${Number(q.value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—'}
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                          {q.expiry_date ? new Date(q.expiry_date).toLocaleDateString('en-GB') : '—'}
                        </td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                          {q.created_at ? new Date(q.created_at).toLocaleDateString('en-GB') : '—'}
                        </td>
                        <td style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(q)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDeleteQuote(q.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            filteredOpps.length === 0 ? <div className="empty-state">No inquiries found</div> : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Customer</th>
                      <th>Stage</th>
                      <th>Est. Value</th>
                      <th>Expected Close</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOpps.map(o => (
                      <tr key={o.id}>
                        <td style={{ fontWeight: 600 }}>{o.title}</td>
                        <td style={{ color: 'var(--text-secondary)' }}>{o.customer_name || '—'}</td>
                        <td><span className={`badge badge-${o.stage}`}>{INQUIRY_STAGES.find(s => s.value === o.stage)?.label || o.stage}</span></td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>
                          {o.estimated_value ? `£${Number(o.estimated_value).toLocaleString('en-GB', { maximumFractionDigits: 0 })}` : '—'}
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                          {o.expected_close ? new Date(o.expected_close).toLocaleDateString('en-GB') : '—'}
                        </td>
                        <td style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(o)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDeleteOpp(o.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )
        )}
      </div>

      {showModal && tab === 'Quotes' && (
        <QuoteModal customers={customers} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />
      )}
      {showModal && tab === 'Inquiries' && (
        <OppModal customers={customers} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />
      )}
      {editing && tab === 'Quotes' && (
        <QuoteModal quote={editing} customers={customers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
        {editing && tab === 'Inquiries' && (
        <OppModal opp={editing} customers={customers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}